/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { InputAdornment } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import React from 'react';
import { dateUtils } from '@opengeoweb/shared';
import { Clock } from '@opengeoweb/theme';
import { ANCHOR_MINUTE_STEP, isValidAnchorTime } from './animationLengthUtils';

export interface AnchorTimeFieldProps {
  value: string;
  label: string;
  onChange: (anchorTime: string) => void;
  disabled?: boolean;
}

const styles = {
  openPickerButton: {
    padding: 0,
  },
  /* Filled inputs offset their adornments for the label, so match that offset. */
  utc: {
    paddingTop: '16px',
  },
};

/**
 * Only the hours and minutes of the carrier are ever read, so its date is arbitrary —
 * but it must be a day no timezone shifts, or a DST gap swallows the anchor's hour.
 */
const TIME_ONLY_CARRIER_DATE = '2001-01-15T00:00:00Z';

/* The picker reads and writes a Date through its local-time getters, so the anchor rides in that frame. */
export const anchorTimeToPickerDate = (anchorTime: string): Date | null => {
  if (!isValidAnchorTime(anchorTime)) {
    return null;
  }
  const [hours, minutes] = anchorTime.split(':').map(Number);
  const pickerDate = new Date(TIME_ONLY_CARRIER_DATE);
  pickerDate.setHours(hours, minutes, 0, 0);
  return pickerDate;
};

export const pickerDateToAnchorTime = (pickerDate: Date): string =>
  dateUtils.dateToString(pickerDate, dateUtils.DATE_FORMAT_HOURS, false)!;

const OpenPicker = (): React.ReactElement => <Clock />;

export const AnchorTimeField: React.FC<AnchorTimeFieldProps> = ({
  value,
  label,
  onChange,
  disabled = false,
}) => {
  /* Typing walks through incomplete times, so only a whole one is worth reporting. */
  const reportCompletedAnchorTime = (pickerDate: Date | null): void => {
    if (pickerDate && dateUtils.isValid(pickerDate)) {
      onChange(pickerDateToAnchorTime(pickerDate));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <TimePicker
        ampm={false}
        closeOnSelect={false}
        desktopModeMediaQuery="@media (min-width: 720px)"
        disabled={disabled}
        label={label}
        onChange={reportCompletedAnchorTime}
        slotProps={{
          actionBar: { actions: ['accept'] },
          field: { openPickerButtonPosition: 'start' },
          openPickerButton: { size: 'small', sx: styles.openPickerButton },
          textField: {
            variant: 'filled',
            slotProps: {
              input: {
                endAdornment: (
                  <InputAdornment position="end" sx={styles.utc}>
                    UTC
                  </InputAdornment>
                ),
              },
            },
          },
        }}
        slots={{ openPickerIcon: OpenPicker }}
        timeSteps={{ minutes: ANCHOR_MINUTE_STEP }}
        value={anchorTimeToPickerDate(value)}
      />
    </LocalizationProvider>
  );
};
